import { Mail, MapPin, Phone, Handshake, Ban } from "lucide-react";
import { deptAvatarClass, deptBadgeClass, effectiveSkills, formatRoDate } from "@/lib/employees";
import { MentorStatusBadge } from "@/components/Mentorship";

const MAX_VISIBLE_SKILLS = 3;

export default function EmployeeCard({ employee, onOpen, onSkillClick }) {
  const { id, name, initials, role, department, email, phone, location, is_collaborator, inactive, inactive_since } = employee;
  const skills = effectiveSkills(employee);
  const visible = skills.slice(0, MAX_VISIBLE_SKILLS);
  const extra = Math.max(0, skills.length - MAX_VISIBLE_SKILLS);

  return (
    <article
      data-testid={`employee-card-${id}`}
      tabIndex={0}
      role="button"
      onClick={() => onOpen(id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen(id);
        }
      }}
      className={`group bg-white dark:bg-zinc-900 border border-border rounded-xl p-5 cursor-pointer hover:-translate-y-1 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100 transition-transform duration-200 flex flex-col ${inactive ? "opacity-60 grayscale-[35%]" : ""}`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-semibold ${deptAvatarClass(department)}`}
        >
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-zinc-900 dark:text-zinc-50 leading-tight truncate">
            {name}
          </h3>
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 line-clamp-2 min-h-[2.5em]">
            {role}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ring-1 ring-inset ${deptBadgeClass(department)}`}>
              {department}
            </span>
            {is_collaborator && (
              <span data-testid={`card-collaborator-${id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 ring-1 ring-inset ring-amber-300/50 dark:ring-amber-800/50">
                <Handshake className="h-3 w-3" /> Colaborator
              </span>
            )}
            {inactive && (
              <span data-testid={`card-inactive-${id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300 ring-1 ring-inset ring-zinc-300 dark:ring-zinc-600">
                <Ban className="h-3 w-3" /> Inactiv{inactive_since ? ` din ${formatRoDate(inactive_since)}` : ""}
              </span>
            )}
            {location && (
              <span data-testid={`card-location-${id}`} className="inline-flex items-center gap-1 text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
                <MapPin className="h-3 w-3" />
                {location}
              </span>
            )}
            <MentorStatusBadge employee={employee} size="sm" />
          </div>
        </div>
      </div>

      {skills.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {visible.map((s) => (
            <button
              key={s}
              data-testid={`card-skill-${id}-${s}`}
              onClick={(e) => { e.stopPropagation(); onSkillClick?.(s); }}
              className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
            >
              {s}
            </button>
          ))}
          {extra > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md text-zinc-500 dark:text-zinc-400">
              +{extra}
            </span>
          )}
        </div>
      )}

      <div className="mt-auto pt-4 border-t border-border flex flex-col gap-1.5 text-sm">
        <a
          href={email ? `mailto:${email}` : undefined}
          onClick={(e) => e.stopPropagation()}
          data-testid={`card-email-${id}`}
          className={`inline-flex items-center gap-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors duration-150 truncate ${!email ? "pointer-events-none opacity-60" : ""}`}
        >
          <Mail className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">{email || "—"}</span>
        </a>
        <a
          href={phone ? `tel:${phone}` : undefined}
          onClick={(e) => e.stopPropagation()}
          data-testid={`card-phone-${id}`}
          className={`inline-flex items-center gap-2 text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors duration-150 ${!phone ? "pointer-events-none opacity-60" : ""}`}
        >
          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
          <span>{phone || "—"}</span>
        </a>
      </div>
    </article>
  );
}
